import type ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import type {FileLoader} from '@ckeditor/ckeditor5-upload/src/filerepository';
import Api from '../../../../core/services/api/api';
import EnvService from '../../../../core/services/env';
import Utils from '../../../../core/services/utils/utils';

/**
 * Upload adapter that sends files through the admin file uploader API
 * and returns the hosted image URL to CKEditor.
 */
class AdminFileUploadAdapter {
    constructor(private readonly loader: FileLoader) {
    }

    /**
     * Uploads the file to the admin file uploader and returns a promise that resolves with the hosted image URL.
     */
    upload(): Promise<{ default: string }> {
        return this.loader.file.then(async (file) => {
            if (!file) {
                throw new Error('Upload failed');
            }

            const formData = new FormData();
            formData.append('file', file, Utils.sanitizeFileName(file.name));

            const response = await Api.uploadFileToAdmin(formData, (progressEvent) => {
                if (progressEvent.lengthComputable) {
                    this.loader.uploadTotal = progressEvent.total;
                    this.loader.uploaded = progressEvent.loaded;
                }
            });

            if (!response?.resultFlag || !response.data?.fileName) {
                throw new Error('Upload failed');
            }

            return {
                default: `${EnvService.AdminFileBaseUrl}${response.data.fileName}`,
            };
        });
    }

    abort(): void {
        // Admin upload requests are not cancellable through the shared API layer.
    }
}

/**
 * Registers the admin upload adapter on the editor's FileRepository.
 * Call from onReady so it overrides any built-in adapters initialized earlier.
 */
export function configureAdminFileUploadAdapter(editor: ClassicEditor): void {
    const fileRepository = editor.plugins.get('FileRepository');
    fileRepository.createUploadAdapter = (loader) => new AdminFileUploadAdapter(loader);
}
